import React, { FC, useEffect, useRef, useState } from 'react';
import { EditFormatChangeProjectModalProps } from './EditFormatChangeProjectModalProps';
import useTranslation from '../../hooks/useTranslation';
import CustomModal from '../../components/CustomModal';
import { ESTABLISHMENT_TYPE } from '../../components/ProjectTypeSelect/utils';
import ProjectForm from '../../components/ProjectForm';
import FormatChangeTabs from '../../components/FormatChangeTabs';
import { useTypedSelector } from '../../store';
import { selectResponsibleOptionsList } from '../../store/users/selectors';
import { getBusinessUnits } from '../../store/selectData/selectors';
import { selectConfidentialPermission } from '../../store/userData/selectors';
import { returnFormatChangeValidationSchema } from '../FormatChangeModal/utils';
import {
  fetchSiteLocation,
  updateProject,
} from '../../store/projects/actionWrapper';
import { useDispatch } from 'react-redux';
import {
  selectSiteLocation,
  selectSolutions,
} from '../../store/projects/selectors';
import { calculateTabErrors } from '../CreateNewUnitModal/utils';
import { ProjectFormRef } from '../../components/ProjectForm/ProjectForm';
import { modalsActions } from '../../store/modals/slice';
import { stripOfNewId } from '../BulkCreate/utils';

const EditFormatChangeProjectModal: FC<EditFormatChangeProjectModalProps> = ({
  projectsList,
  project,
  closeHandler,
  establishmentType,
  countries,
  setActiveProject,
  disableRestrictions,
  additionalAction,
  readonly,
  submitTrigger,
  directiveOpen: initialDirectiveOpen,
}) => {
  const dispatch = useDispatch();

  const t = useTranslation();
  const isConfidentialAllowed = useTypedSelector(selectConfidentialPermission);
  const BuList = useTypedSelector(getBusinessUnits);
  const usersList = useTypedSelector(selectResponsibleOptionsList);
  const validationSchema = returnFormatChangeValidationSchema(t, project?.isep);
  const solutions = useTypedSelector(selectSolutions);
  const [formModified, setFormModified] = React.useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = React.useState(
    project?.countryCode,
  );
  const [statusUpdateConfirmationModal, setStatusUpdateConfiramtionModal] =
    useState(false);
  const [directiveOpen, setDirectiveOpen] = useState(initialDirectiveOpen);
  useEffect(() => {
    setDirectiveOpen(initialDirectiveOpen);
  }, [initialDirectiveOpen, establishmentType]);
  const formRef = useRef<ProjectFormRef>(null);
  const selectedSiteLocation = useTypedSelector(selectSiteLocation);
  if (!project) return <></>;

  const onCountryChange = (country) => {
    setSelectedCountry(country.target.value);
  };
  const editCloseProjectHandler = (data) => {
    submitTrigger();
    dispatch(
      updateProject({
        project: {
          ...data,
          solutions: solutions.map(stripOfNewId),
          establishmentType: ESTABLISHMENT_TYPE.FORMAT_CHANGE,
        },
        action: additionalAction,
      }),
    );
    setFormModified(false);
    closeHandler();
  };

  const onBUChange = (bu) => {
    dispatch(fetchSiteLocation(bu.businessUnitId || ''));
  };

  const BuFiltered = BuList.filter(
    (bu) => bu.countryId === (selectedCountry || project?.countryCode),
  );

  const openStatusConfirmationModal = () => {
    setStatusUpdateConfiramtionModal(true);
  };
  const closeStatusConfirmationModal = () => {
    setStatusUpdateConfiramtionModal(false);
  };

  const updateStatusHandler = (id) => {
    setActiveProject(id);
    dispatch(modalsActions.openProjectStatusModal());
  };

  const updateStatusClicked = (id: string) => {
    if (formModified) {
      openStatusConfirmationModal();
      return;
    }
    updateStatusHandler(id);
  };

  const proceedWithStatusChange = () => {
    formRef.current.submit();
    closeStatusConfirmationModal();
    updateStatusHandler(project.id);
  };

  return (
    <CustomModal
      isVisible={establishmentType === ESTABLISHMENT_TYPE.FORMAT_CHANGE}
      onClose={closeHandler}
      formModified={formModified}
      title={
        directiveOpen ? t('labels.projectDirective') : t('common.editProject')
      }
      onBackBtnClick={directiveOpen ? () => setDirectiveOpen(false) : undefined}
      useConfirmation
      statusConfirmationModal={statusUpdateConfirmationModal}
      closeStatusConfirmationModal={closeStatusConfirmationModal}
      proceedStatusUpdate={proceedWithStatusChange}
      children={
        <ProjectForm
          onSubmit={editCloseProjectHandler}
          project={project}
          ref={formRef}
          directiveOpen={directiveOpen}
          onDirectiveBack={() => setDirectiveOpen(false)}
          onClose={closeHandler}
          setFormModified={(isDirty) => setFormModified(isDirty)}
          isEdit
          submitBtnTitle={t('common.update')}
          selectedSiteLocation={selectedSiteLocation}
          validationSchema={validationSchema}
          projectType={ESTABLISHMENT_TYPE.FORMAT_CHANGE}
          setFormErrors={setFormErrors}
          disableRestrictions={disableRestrictions}
          readOnly={readonly}
        >
          <FormatChangeTabs
            projectsList={projectsList}
            currentProject={project}
            countries={countries}
            setActiveProject={updateStatusClicked}
            usersList={usersList}
            onCountryChange={onCountryChange}
            onBUChange={onBUChange}
            BuList={BuFiltered}
            isConfidentialAllowed={isConfidentialAllowed}
            tabErrors={calculateTabErrors(formErrors, true)}
            isEdit
            directiveOpen={directiveOpen}
            onShowDirective={() => setDirectiveOpen(true)}
            onHideDirective={() => setDirectiveOpen(false)}
          />
        </ProjectForm>
      }
    />
  );
};

export default EditFormatChangeProjectModal;
